/**
 * assistantAlerts.js
 * 
 * Motor de alertas inteligentes para el Asistente del applicant.
 * Genera una lista priorizada de alertas/acciones basándose en:
 *   - Contratos y aplicaciones del usuario
 *   - Notificaciones de Firestore
 *   - Estado del perfil y documentos
 *   - Chats e incidencias
 *   - Pagos pendientes (con paymentConfirmations)
 *   - Renovaciones sugeridas (con chequeo de pendientes)
 *   - Recomendaciones de colivings
 */

// ══════════════════════════════════════════════════
// PRIORIDADES
// ══════════════════════════════════════════════════

const PRIORITY = {
  critical: 0,   // Pago vencido, contrato por expirar hoy
  high: 1,       // Pago próximo, firma pendiente, pre-aprobación
  medium: 2,     // Mensajes sin leer, incidencias, perfil incompleto
  low: 3,        // Sugerencias, recordatorios suaves
};

// ══════════════════════════════════════════════════
// HELPERS DE PAGO
// ══════════════════════════════════════════════════

/**
 * Determina el número de mes actual de la estancia (1-based).
 * Mes 1 = primer mes (cubierto por pago initial).
 */
function getCurrentStayMonth(desiredStartDate) {
  if (!desiredStartDate) return 0;
  const start = typeof desiredStartDate === 'string' ? new Date(desiredStartDate) : desiredStartDate;
  if (isNaN(start.getTime())) return 0;

  const now = new Date();
  const diffMs = now - start;
  if (diffMs < 0) return 0; // Aún no ha empezado

  // Calcular meses transcurridos (1-based)
  const monthsDiff = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return monthsDiff + 1; // Mes 1 = primer mes
}

/**
 * Genera el paymentId para un mes dado.
 * Mes 1 → 'initial', Mes 2 → 'month-2', Mes 3 → 'month-3', etc.
 */
function getPaymentIdForMonth(monthNumber) {
  if (monthNumber <= 1) return 'initial';
  return `month-${monthNumber}`;
}

/**
 * Comprueba si un pago está confirmado en las paymentConfirmations.
 */
function isPaymentConfirmed(paymentConfirmations, paymentId) {
  if (!paymentConfirmations || !Array.isArray(paymentConfirmations)) return false;
  return paymentConfirmations.some(
    (p) => p.paymentId === paymentId && p.confirmed === true
  );
}

// ══════════════════════════════════════════════════
// GENERADORES DE ALERTAS
// ══════════════════════════════════════════════════

/**
 * Genera alertas basadas en contratos/aplicaciones del usuario.
 * 
 * Contexto adicional:
 *   - chats: para mapear chatId por applicationId
 *   - renewalStatuses: mapa { colivingId: { hasPendingRenewal: bool } }
 */
function generateContractAlerts(contracts, currentUserId, { chats = [], renewalStatuses = {} } = {}) {
  const alerts = [];
  if (!contracts || !contracts.length) return alerts;

  const now = new Date();

  // Construir mapa applicationId → chatId desde los chats suscritos
  const chatByApplicationId = {};
  chats.forEach((chat) => {
    if (chat.applicationId) {
      chatByApplicationId[chat.applicationId] = chat.id;
    }
  });

  contracts.forEach((contract) => {
    const isColiving = contract.type === 'coliving';
    // Ignorar solicitudes retiradas
    if (contract.applicationStatus === 'retired') return;

    const appData = contract.applicationData || {};
    const paymentConfirmations = appData.paymentConfirmations || [];
    const applicationId = contract.applicationId || '';

    // Resolver chatId: primero del contrato, luego del mapa de chats
    const resolvedChatId = contract.chatId || chatByApplicationId[applicationId] || '';

    // ══════════════════════════════════════════════════
    // ESTANCIA ACTIVA (room_assigned)
    // ══════════════════════════════════════════════════
    if (isColiving && contract.applicationStatus === 'room_assigned') {
      const endDate = contract.endDate || contract.desiredEndDate || appData.calculatedEndDate || appData.desiredEndDate;
      const startDate = appData.desiredStartDate || contract.desiredStartDate;
      const stayDuration = appData.stayDurationMonths || contract.stayDurationMonths || 0;
      const price = contract.propertyPrice || contract.monthlyRent || appData.monthlyRent || 0;
      const colivingId = contract.colivingId || contract.id;

      // ── Tiempo restante de estancia ──
      if (endDate) {
        const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
        const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) {
          alerts.push({
            id: `stay-expired-${contract.id}`,
            type: 'stay_expiring',
            priority: PRIORITY.critical,
            icon: 'clock',
            color: 'red',
            title: contract.title || 'Tu coliving',
            message: 'Tu estancia ha terminado. Contacta con tu agente.',
            action: { type: 'navigate', path: `/contracts/${contract.id}` },
          });
        } else if (diffDays <= 7) {
          alerts.push({
            id: `stay-ending-soon-${contract.id}`,
            type: 'stay_expiring',
            priority: PRIORITY.critical,
            icon: 'clock',
            color: 'amber',
            title: contract.title || 'Tu coliving',
            message: `Tu estancia termina en ${diffDays} día${diffDays !== 1 ? 's' : ''}.`,
            action: { type: 'navigate', path: `/contracts/${contract.id}` },
          });
        } else if (diffDays <= 30) {
          alerts.push({
            id: `stay-ending-${contract.id}`,
            type: 'stay_expiring',
            priority: PRIORITY.medium,
            icon: 'calendar',
            color: 'blue',
            title: contract.title || 'Tu coliving',
            message: `Tu estancia termina en ${diffDays} días.`,
            action: { type: 'navigate', path: `/contracts/${contract.id}` },
          });
        }

        // ── Renovación proactiva ──
        // Solo si NO hay renovación pendiente y duración < 11 meses
        const hasPendingRenewal = renewalStatuses[colivingId]?.hasPendingRenewal === true;
        if (diffDays <= 30 && diffDays > 0 && stayDuration < 11 && !hasPendingRenewal) {
          alerts.push({
            id: `renewal-suggest-${contract.id}`,
            type: 'renewal',
            priority: diffDays <= 7 ? PRIORITY.high : PRIORITY.medium,
            icon: 'refresh',
            color: 'teal',
            title: '¿Quieres renovar tu estancia?',
            message: `Tu estancia en ${contract.title || 'tu coliving'} termina en ${diffDays} días. Puedes solicitar una renovación.`,
            action: {
              type: 'renewal',
              colivingId,
              applicationId,
              colivingTitle: contract.title || '',
              currentStayMonths: stayDuration,
            },
          });
        }

        // Si ya tiene renovación pendiente, informar
        if (hasPendingRenewal) {
          alerts.push({
            id: `renewal-pending-${contract.id}`,
            type: 'renewal_pending',
            priority: PRIORITY.low,
            icon: 'refresh',
            color: 'teal',
            title: 'Renovación en revisión',
            message: `Tu solicitud de renovación para ${contract.title || 'tu coliving'} está pendiente de aprobación.`,
            action: { type: 'navigate', path: `/colivings/${colivingId}` },
          });
        }

        // ── Sugerir colivings nuevos al acabar estancia (≤ 14 días) ──
        if (diffDays <= 14 && diffDays > 0) {
          alerts.push({
            id: `suggest-colivings-${contract.id}`,
            type: 'suggestions',
            priority: PRIORITY.low,
            icon: 'home',
            color: 'indigo',
            title: 'Explora nuevos colivings',
            message: 'Tu estancia termina pronto. Mira colivings que se ajustan a tu perfil.',
            action: { type: 'view-recommendations' },
          });
        }
      }

      // ══════════════════════════════════════════════════
      // PAGOS INTELIGENTES (basado en paymentConfirmations)
      // ══════════════════════════════════════════════════
      if (price > 0 && startDate) {
        const currentMonth = getCurrentStayMonth(startDate);

        if (currentMonth > 0 && currentMonth <= stayDuration) {
          const currentPaymentId = getPaymentIdForMonth(currentMonth);
          const isPaid = isPaymentConfirmed(paymentConfirmations, currentPaymentId);

          if (!isPaid) {
            const dayOfMonth = now.getDate();

            if (dayOfMonth > 5) {
              // ── PAGO ATRASADO: ya pasó el día 5 y no ha pagado ──
              alerts.push({
                id: `payment-overdue-${contract.id}-m${currentMonth}`,
                type: 'payment',
                priority: PRIORITY.critical,
                icon: 'payment',
                color: 'red',
                title: `Pago atrasado: ${price}€`,
                message: `El pago del mes ${currentMonth} en ${contract.title || 'tu coliving'} venció el día 5. Paga cuanto antes.`,
                action: {
                  type: 'payment',
                  colivingId,
                  applicationId,
                  amount: price,
                  path: `/colivings/${colivingId}`,
                },
              });
            } else {
              // ── PAGO PRÓXIMO: aún está a tiempo ──
              const daysLeft = 5 - dayOfMonth;
              alerts.push({
                id: `payment-due-${contract.id}-m${currentMonth}`,
                type: 'payment',
                priority: PRIORITY.high,
                icon: 'payment',
                color: 'emerald',
                title: `Pago pendiente: ${price}€`,
                message: daysLeft > 0
                  ? `Tu pago del mes ${currentMonth} en ${contract.title || 'tu coliving'} vence en ${daysLeft} día${daysLeft !== 1 ? 's' : ''}.`
                  : `Tu pago del mes ${currentMonth} en ${contract.title || 'tu coliving'} vence hoy.`,
                action: {
                  type: 'payment',
                  colivingId,
                  applicationId,
                  amount: price,
                  path: `/colivings/${colivingId}`,
                },
              });
            }
          }

          // ── AVISO DEL MES SIGUIENTE (últimos 5 días del mes actual) ──
          const nextMonth = currentMonth + 1;
          if (nextMonth <= stayDuration) {
            const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const daysLeftInMonth = daysInCurrentMonth - now.getDate();

            if (daysLeftInMonth <= 5) {
              const nextPaymentId = getPaymentIdForMonth(nextMonth);
              const isNextPaid = isPaymentConfirmed(paymentConfirmations, nextPaymentId);

              if (!isNextPaid) {
                alerts.push({
                  id: `payment-upcoming-${contract.id}-m${nextMonth}`,
                  type: 'payment',
                  priority: PRIORITY.medium,
                  icon: 'payment',
                  color: 'blue',
                  title: `Próximo pago: ${price}€`,
                  message: `El pago del mes ${nextMonth} en ${contract.title || 'tu coliving'} se acerca (día 5).`,
                  action: {
                    type: 'payment',
                    colivingId,
                    applicationId,
                    amount: price,
                    path: `/colivings/${colivingId}`,
                  },
                });
              }
            }
          }
        }
      }

      // ── SEPA pendiente ──
      const sepaMandateRequired = contract.sepaMandateRequired || appData.sepaMandateRequired;
      const hasSepaMandateInfo = !!(appData.sepaMandateInfo && appData.sepaMandateInfo.applicantBankNumber);
      if (sepaMandateRequired && !hasSepaMandateInfo) {
        alerts.push({
          id: `sepa-pending-${contract.id}`,
          type: 'sepa',
          priority: PRIORITY.high,
          icon: 'payment',
          color: 'amber',
          title: 'Mandato SEPA pendiente',
          message: `Completa el mandato SEPA en ${contract.title || 'tu coliving'} para activar los pagos recurrentes.`,
          action: {
            type: 'navigate',
            path: `/colivings/${colivingId}`,
          },
        });
      }

      // ── Incidencia rápida (con chatId resuelto) ──
      if (resolvedChatId) {
        alerts.push({
          id: `quick-incident-${contract.id}`,
          type: 'quick_action',
          priority: PRIORITY.low + 1,
          icon: 'wrench',
          color: 'orange',
          title: 'Reportar incidencia',
          message: `¿Algo roto o un problema en ${contract.title || 'tu coliving'}? Reporta aquí.`,
          action: {
            type: 'report-incident',
            chatId: resolvedChatId,
            colivingTitle: contract.title || '',
          },
        });
      }
    }

    // ══════════════════════════════════════════════════
    // CONTRATO PENDIENTE DE FIRMA
    // ══════════════════════════════════════════════════
    if (isColiving && contract.applicationStatus === 'pre_approved') {
      alerts.push({
        id: `sign-pending-${contract.id}`,
        type: 'sign',
        priority: PRIORITY.high,
        icon: 'pen',
        color: 'indigo',
        title: 'Contrato por firmar',
        message: `${contract.title || 'Un coliving'} te ha pre-aprobado. Firma el contrato.`,
        action: { type: 'navigate', path: `/colivings/${contract.colivingId || contract.id}` },
      });
    }

    // ══════════════════════════════════════════════════
    // PAGO INICIAL PENDIENTE
    // ══════════════════════════════════════════════════
    if (isColiving && contract.applicationStatus === 'contract_signed') {
      const initialPaid = isPaymentConfirmed(appData.paymentConfirmations, 'initial');
      if (!initialPaid) {
        alerts.push({
          id: `initial-payment-${contract.id}`,
          type: 'payment',
          priority: PRIORITY.high,
          icon: 'payment',
          color: 'emerald',
          title: 'Pago inicial pendiente',
          message: `Completa el primer pago en ${contract.title || 'tu coliving'} para confirmar tu plaza.`,
          action: {
            type: 'payment',
            colivingId: contract.colivingId || contract.id,
            applicationId: contract.applicationId || contract.id,
            path: `/colivings/${contract.colivingId || contract.id}`,
          },
        });
      }
    }

    // ══════════════════════════════════════════════════
    // APLICACIONES EN PROGRESO + SIN RESPUESTA
    // ══════════════════════════════════════════════════
    if (isColiving && (contract.applicationStatus === 'submitted' || contract.applicationStatus === 'under_review' || !contract.applicationStatus)) {
      const createdAt = appData.createdAt || contract.createdAt;
      let daysSinceSubmission = 0;
      if (createdAt) {
        const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        daysSinceSubmission = Math.floor((now - created) / (1000 * 60 * 60 * 24));
      }

      if (daysSinceSubmission > 7) {
        // ── Solicitud sin respuesta (> 7 días) ──
        alerts.push({
          id: `app-no-response-${contract.id}`,
          type: 'application_stale',
          priority: PRIORITY.medium,
          icon: 'clock',
          color: 'amber',
          title: 'Solicitud sin respuesta',
          message: `Tu solicitud para ${contract.title || 'un coliving'} lleva ${daysSinceSubmission} días sin respuesta.`,
          action: {
            type: 'withdraw-or-view',
            colivingId: contract.colivingId || contract.id,
            applicationId,
            colivingTitle: contract.title || '',
            daysPending: daysSinceSubmission,
          },
        });
      } else {
        // ── Solicitud en revisión (normal) ──
        alerts.push({
          id: `app-pending-${contract.id}`,
          type: 'application',
          priority: PRIORITY.low,
          icon: 'doc',
          color: 'gray',
          title: 'Solicitud en revisión',
          message: `Tu solicitud para ${contract.title || 'un coliving'} está siendo revisada.`,
          action: { type: 'navigate', path: `/colivings/${contract.colivingId || contract.id}` },
        });
      }
    }
  });

  return alerts;
}

/**
 * Genera alertas basadas en el estado del perfil del usuario.
 */
function generateProfileAlerts(profileData) {
  const alerts = [];
  if (!profileData) return alerts;

  const { overallProgress, hasGuarantor, guarantorInfo, documentsProgress } = profileData;

  if (overallProgress < 100) {
    const priority = overallProgress < 30 ? PRIORITY.high : PRIORITY.medium;
    alerts.push({
      id: 'profile-incomplete',
      type: 'profile',
      priority,
      icon: 'user',
      color: 'violet',
      title: `Perfil ${overallProgress}% completado`,
      message: 'Completa tu perfil para mejorar tus posibilidades de ser seleccionado.',
      action: { type: 'navigate', path: '/user-dashboard?section=profile' },
    });
  }

  if (documentsProgress < 100) {
    alerts.push({
      id: 'docs-incomplete',
      type: 'documents',
      priority: PRIORITY.medium,
      icon: 'doc',
      color: 'amber',
      title: 'Documentos pendientes',
      message: 'Sube tus documentos para agilizar tus solicitudes.',
      action: { type: 'navigate', path: '/user-dashboard?section=documents' },
    });
  }

  if (!hasGuarantor) {
    alerts.push({
      id: 'no-guarantor',
      type: 'guarantor',
      priority: PRIORITY.low,
      icon: 'shield',
      color: 'emerald',
      title: 'Añade un avalista',
      message: 'El 85% de los agentes prefieren candidatos con avalista.',
      action: { type: 'navigate', path: '/user-dashboard?section=guarantor' },
    });
  } else if (guarantorInfo?.status === 'pending') {
    alerts.push({
      id: 'guarantor-pending',
      type: 'guarantor',
      priority: PRIORITY.medium,
      icon: 'shield',
      color: 'amber',
      title: 'Avalista pendiente',
      message: `${guarantorInfo.fullName || 'Tu avalista'} aún no ha verificado su identidad.`,
      action: { type: 'navigate', path: '/user-dashboard?section=guarantor' },
    });
  } else if (guarantorInfo?.status === 'rejected') {
    alerts.push({
      id: 'guarantor-rejected',
      type: 'guarantor',
      priority: PRIORITY.high,
      icon: 'shield',
      color: 'red',
      title: 'Avalista rechazado',
      message: `La verificación de ${guarantorInfo.fullName || 'tu avalista'} fue rechazada. Actualiza los datos.`,
      action: { type: 'navigate', path: '/user-dashboard?section=guarantor' },
    });
  }

  return alerts;
}

/**
 * Genera alertas basadas en chats y mensajes sin leer.
 */
function generateChatAlerts(chats) {
  const alerts = [];
  if (!chats || !chats.length) return alerts;

  let totalUnread = 0;
  const chatsWithUnread = [];

  chats.forEach((chat) => {
    const unread = chat.unreadCount || 0;
    if (unread > 0) {
      totalUnread += unread;
      chatsWithUnread.push(chat);
    }
  });

  if (totalUnread > 0) {
    const firstChat = chatsWithUnread[0];
    alerts.push({
      id: 'unread-messages',
      type: 'chat',
      priority: PRIORITY.medium,
      icon: 'chat',
      color: 'blue',
      title: `${totalUnread} mensaje${totalUnread !== 1 ? 's' : ''} sin leer`,
      message: chatsWithUnread.length === 1
        ? `De ${firstChat.contactName || 'tu agente'}`
        : `En ${chatsWithUnread.length} conversaciones`,
      action: { type: 'open-chat' },
    });
  }

  return alerts;
}

/**
 * Convierte notificaciones de Firestore a alertas del asistente.
 */
function generateNotificationAlerts(notifications) {
  const alerts = [];
  if (!notifications || !notifications.length) return alerts;

  const unread = notifications.filter((n) => !n.read);

  const typeConfig = {
    coliving_pre_approved: { priority: PRIORITY.high, icon: 'star', color: 'purple', label: 'Pre-aprobado' },
    coliving_assignment_confirmed: { priority: PRIORITY.high, icon: 'check', color: 'emerald', label: 'Asignación confirmada' },
    coliving_contract_completed: { priority: PRIORITY.high, icon: 'pen', color: 'indigo', label: 'Contrato completado' },
    coliving_applicant_signed: { priority: PRIORITY.medium, icon: 'pen', color: 'blue', label: 'Firma registrada' },
    coliving_payment_completed: { priority: PRIORITY.medium, icon: 'payment', color: 'emerald', label: 'Pago registrado' },
    coliving_renewal_submitted: { priority: PRIORITY.medium, icon: 'refresh', color: 'teal', label: 'Renovación solicitada' },
    assigned_to_you: { priority: PRIORITY.high, icon: 'star', color: 'indigo', label: 'Asignado a ti' },
    owner_signed: { priority: PRIORITY.medium, icon: 'pen', color: 'blue', label: 'Propietario firmó' },
    application_deleted: { priority: PRIORITY.low, icon: 'info', color: 'gray', label: 'Solicitud actualizada' },
  };

  unread.slice(0, 5).forEach((notif) => {
    const config = typeConfig[notif.type] || {
      priority: PRIORITY.low,
      icon: 'info',
      color: 'gray',
      label: 'Notificación',
    };

    alerts.push({
      id: `notif-${notif.id}`,
      notificationId: notif.id,
      type: 'notification',
      priority: config.priority,
      icon: config.icon,
      color: config.color,
      title: config.label,
      message: notif.message || '',
      contractTitle: notif.contractTitle || null,
      timestamp: notif.timestamp,
      action: notif.contractId
        ? { type: 'navigate', path: `/contracts/${notif.contractId}` }
        : null,
    });
  });

  return alerts;
}

// ══════════════════════════════════════════════════
// ONBOARDING (primeros pasos)
// ══════════════════════════════════════════════════

/**
 * Genera alertas de bienvenida/onboarding para applicants nuevos.
 * Se muestra solo cuando el usuario no tiene contratos activos
 * y su perfil está muy incompleto (< 50%).
 */
function generateOnboardingAlerts(contracts, profileData) {
  const alerts = [];

  // Solo mostrar onboarding si no tiene ningún contrato de coliving
  const hasColivingContracts = contracts && contracts.some((c) => c.type === 'coliving');
  if (hasColivingContracts) return alerts;

  const progress = profileData?.overallProgress || 0;
  const hasDocuments = (profileData?.documentsProgress || 0) > 0;
  const hasGuarantor = profileData?.hasGuarantor || false;

  // Solo si el perfil está por debajo del 50% — es un usuario "nuevo"
  if (progress >= 50) return alerts;

  // Alerta de bienvenida (siempre primera)
  alerts.push({
    id: 'onboarding-welcome',
    type: 'onboarding',
    priority: PRIORITY.medium,
    icon: 'star',
    color: 'indigo',
    title: '¡Bienvenido a Coolliving!',
    message: 'Sigue estos pasos para encontrar tu coliving ideal.',
    action: null,
  });

  // Paso 1: Completar perfil
  if (progress < 30) {
    alerts.push({
      id: 'onboarding-profile',
      type: 'onboarding',
      priority: PRIORITY.medium,
      icon: 'user',
      color: 'violet',
      title: 'Paso 1: Completa tu perfil',
      message: 'Los agentes ven tu perfil al revisar solicitudes. Un perfil completo multiplica tus opciones.',
      action: { type: 'navigate', path: '/user-dashboard?section=profile' },
    });
  }

  // Paso 2: Subir documentos
  if (!hasDocuments) {
    alerts.push({
      id: 'onboarding-docs',
      type: 'onboarding',
      priority: PRIORITY.medium,
      icon: 'doc',
      color: 'amber',
      title: 'Paso 2: Sube tus documentos',
      message: 'Nóminas, contrato de trabajo... Tener la documentación lista acelera el proceso.',
      action: { type: 'navigate', path: '/user-dashboard?section=documents' },
    });
  }

  // Paso 3: Añadir avalista
  if (!hasGuarantor) {
    alerts.push({
      id: 'onboarding-guarantor',
      type: 'onboarding',
      priority: PRIORITY.low,
      icon: 'shield',
      color: 'emerald',
      title: 'Paso 3: Añade un avalista',
      message: 'El 85% de los agentes prefieren candidatos con avalista. Mejora tu candidatura.',
      action: { type: 'navigate', path: '/user-dashboard?section=guarantor' },
    });
  }

  // Paso 4: Buscar colivings
  alerts.push({
    id: 'onboarding-search',
    type: 'onboarding',
    priority: PRIORITY.low,
    icon: 'home',
    color: 'blue',
    title: 'Paso 4: Busca tu coliving',
    message: 'Explora colivings que se ajusten a tu presupuesto y ubicación.',
    action: { type: 'navigate', path: '/encuentratucoliving' },
  });

  return alerts;
}

// ══════════════════════════════════════════════════
// FUNCIÓN PRINCIPAL
// ══════════════════════════════════════════════════

/**
 * Genera todas las alertas del asistente, las prioriza y devuelve
 * un máximo de `limit` alertas ordenadas por prioridad.
 * 
 * @param {Object} params
 * @param {Array}  params.contracts - Contratos/aplicaciones del usuario
 * @param {string} params.currentUserId
 * @param {Array}  params.notifications - Notificaciones de Firestore
 * @param {Array}  params.chats - Chats suscritos (con applicationId)
 * @param {Object} params.profileData
 * @param {Object} params.renewalStatuses - Mapa { colivingId: { hasPendingRenewal } }
 * @param {number} limit
 */
export function generateAssistantAlerts({
  contracts = [],
  currentUserId = '',
  notifications = [],
  chats = [],
  profileData = null,
  renewalStatuses = {},
  newRecommendationsCount = 0,
} = {}, limit = 15) {
  const all = [
    ...generateContractAlerts(contracts, currentUserId, { chats, renewalStatuses }),
    ...generateNotificationAlerts(notifications),
    ...generateChatAlerts(chats),
    ...generateProfileAlerts(profileData),
    ...generateOnboardingAlerts(contracts, profileData),
  ];

  // ── Nuevas recomendaciones de colivings (proactivo) ──
  if (newRecommendationsCount > 0) {
    all.push({
      id: 'new-recommendations',
      type: 'new_recommendations',
      priority: PRIORITY.low,
      icon: 'home',
      color: 'indigo',
      title: `${newRecommendationsCount} nuevo${newRecommendationsCount !== 1 ? 's' : ''} coliving${newRecommendationsCount !== 1 ? 's' : ''} para ti`,
      message: 'Hemos encontrado colivings que encajan con tu perfil. ¡Échalos un vistazo!',
      action: { type: 'view-recommendations' },
    });
  }

  // Deduplicar por id
  const unique = [];
  const seen = new Set();
  for (const alert of all) {
    if (!seen.has(alert.id)) {
      seen.add(alert.id);
      unique.push(alert);
    }
  }

  // Ordenar por prioridad (menor número = mayor prioridad)
  unique.sort((a, b) => a.priority - b.priority);

  return unique.slice(0, limit);
}

/**
 * Cuenta las alertas urgentes (critical + high).
 */
export function countUrgentAlerts(alerts) {
  return alerts.filter((a) => a.priority <= PRIORITY.high).length;
}

/**
 * Extrae los contratos activos (room_assigned) con chat disponible.
 * Ahora también resuelve chatId desde el array de chats si no viene en el contrato.
 */
export function getActiveStaysWithChat(contracts, chats = []) {
  if (!contracts || !contracts.length) return [];

  // Mapa applicationId → chatId
  const chatByAppId = {};
  chats.forEach((chat) => {
    if (chat.applicationId) {
      chatByAppId[chat.applicationId] = chat.id;
    }
  });

  return contracts
    .filter((c) => c.type === 'coliving' && c.applicationStatus === 'room_assigned')
    .map((c) => {
      const chatId = c.chatId || chatByAppId[c.applicationId] || '';
      return chatId ? {
        contractId: c.id,
        chatId,
        colivingId: c.colivingId || c.id,
        title: c.title || 'Coliving',
      } : null;
    })
    .filter(Boolean);
}

/**
 * Determina si el usuario tiene estancias que terminan pronto (≤ 14 días).
 */
export function hasEndingStays(contracts) {
  if (!contracts || !contracts.length) return false;
  const now = new Date();
  return contracts.some((c) => {
    if (c.type !== 'coliving' || c.applicationStatus !== 'room_assigned') return false;
    const endDate = c.endDate || c.desiredEndDate;
    if (!endDate) return false;
    const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 14;
  });
}
